<template>
  <div>
    <Header />
    <div id="wrapper" class="booking-process hw-page" style="position: relative">
      <div class="main only-hotel hw-main">
        <!-- ================== MODO VITRINA (hub) ================== -->
        <div v-if="mode === 'hub'" class="hw-hub">
          <div class="hw-hub-head">
            <h1 class="hw-title">Holiday Weeks</h1>
            <p class="hw-sub">{{ $func.parseIso("holidayIntro") }}</p>
            <small class="hw-hint">{{ $func.parseIso("holidayNext12") }}</small>
          </div>

          <!-- Panel de busqueda: holiday principal, destino opcional -->
          <div class="hw-panel">
            <div class="hw-field">
              <label>{{ $func.parseIso("holiday") }}</label>
              <select v-model="panel.holiday">
                <option value="all">{{ $func.parseIso("anyHoliday") }}</option>
                <option v-for="h in catalogHolidays" :key="h.token + h.year" :value="h.token + '|' + h.year">
                  {{ holidayLabel(h.token, iso) }} · {{ h.yearLabel }}
                </option>
              </select>
            </div>
            <div class="hw-field">
              <label>{{ $func.parseIso("holidayDestination") }}</label>
              <select v-model="panel.country">
                <option value="">{{ $func.parseIso("anywhere") }}</option>
                <option v-for="c in countries" :key="c" :value="c">{{ c }}</option>
              </select>
            </div>
            <div class="hw-field">
              <label>{{ $func.parseIso("guests") }}</label>
              <select v-model="panel.occupancy">
                <option value="">{{ $func.parseIso("all") }}</option>
                <option v-for="n in [2, 4, 6, 8]" :key="n" :value="n">{{ n }}+</option>
              </select>
            </div>
            <button type="button" class="hw-search-btn" :style="btnStyle" @click="submitPanel">
              {{ $func.parseIso("searchHolidayWeeks") }}
            </button>
          </div>
          <div class="hw-alt-row">
            <router-link to="/">{{ $func.parseIso("hwRegularWeeks") }}</router-link>
          </div>

          <!-- Vitrina por grupos -->
          <div v-if="catalogLoading" class="hw-loading">
            {{ $func.parseIso("loadingResortName") }}...
          </div>
          <section v-for="group in groupsWithHolidays" :key="group.id" class="hw-group">
            <div class="hw-group-head">
              <h2>{{ groupTitle(group.id) }}</h2>
            </div>
            <div class="hw-grid" :class="{ compact: group.id !== 'major' }">
              <button
                v-for="h in group.holidays"
                :key="h.token + h.year"
                type="button"
                class="hw-card"
                :class="[h.state, { compact: group.id !== 'major' }]"
                @click="goToResults(h)"
              >
                <span class="hw-card-when">{{ formatWhen(h) }}</span>
                <span v-if="h.nextUp" class="hw-next-badge">{{ $func.parseIso("nextUp") }}</span>
                <span class="hw-card-body">
                  <span class="hw-card-name">{{ holidayLabel(h.token, iso) }}</span>
                  <span class="hw-card-year">{{ h.yearLabel }}</span>
                  <span class="hw-card-foot">
                    <span class="hw-state" :class="h.state"><i></i>{{ stateLabel(h.state) }}</span>
                    <span>{{ countLabel(h) }}</span>
                  </span>
                </span>
              </button>
            </div>
          </section>
        </div>

        <!-- ================== MODO RESULTADOS ================== -->
        <div v-else class="hw-results">
          <div class="hw-crumb">
            <a href="#" @click.prevent="goHub">Holiday Weeks</a>
            <span> / {{ resultsTitle }}</span>
            <span class="hw-crumb-sep">·</span>
            <router-link to="/">{{ $func.parseIso("hwRegularWeeks") }}</router-link>
          </div>

          <div class="hw-res-head">
            <h1 class="hw-title">{{ resultsTitle }}</h1>
            <p class="hw-sub">{{ resultsSub }}</p>
          </div>

          <div class="hw-summary">
            <div>
              <small>{{ $func.parseIso("holiday") }}</small>
              <strong>{{ summaryHoliday }}</strong>
            </div>
            <div>
              <small>{{ $func.parseIso("holidayDestination") }}</small>
              <strong>{{ summaryDestination }}</strong>
            </div>
            <div>
              <small>{{ $func.parseIso("guests") }}</small>
              <strong>{{ summaryGuests }}</strong>
            </div>
            <button type="button" class="hw-modify" @click="goHub">
              {{ $func.parseIso("modifySearch") }}
            </button>
          </div>

          <!-- Poco inventario: aviso + Notify me -->
          <div v-if="isLowInventory" class="hw-banner">
            <div>
              <b>{{ $func.parseIso("nWeeksLeft") }} {{ occurrence.count }} {{ $func.parseIso("weeksLeftFor") }} {{ resultsTitle }}.</b>
              <span>{{ $func.parseIso("notifyHint") }}</span>
            </div>
            <button v-if="!isNotified" type="button" class="hw-search-btn sm" @click="notify">
              {{ $func.parseIso("notifyMe") }}
            </button>
            <span v-else class="hw-notify-done">{{ $func.parseIso("notifyDone") }}</span>
          </div>

          <div class="hw-layout">
            <HolidayFilters
              :search="search"
              :facets="facets"
              :catalog="catalog"
              :mode="holMode"
              @apply="applyFilters"
            />

            <section class="hw-list-col">
              <div class="hw-bar">
                <div class="hw-count"><b>{{ totalList }}</b> {{ $func.parseIso("hwResults") }}</div>
                <div class="hw-bar-right">
                  <div class="hw-seg">
                    <button type="button" :class="{ on: search.view === 'list' }" @click="setView('list')">
                      {{ $func.parseIso("list") }}
                    </button>
                    <button type="button" :class="{ on: search.view === 'dest' }" @click="setView('dest')">
                      {{ $func.parseIso("byDestination") }}
                    </button>
                  </div>
                  <select class="hw-sort" v-model="sortModel" @change="onSortChange">
                    <option value="ci">{{ $func.parseIso("hwCheckIn") }} ↑</option>
                    <option value="dest">{{ $func.parseIso("holidayDestination") }} A–Z</option>
                  </select>
                </div>
              </div>

              <!-- Vista By destination: tiles con conteo, sin precios -->
              <div v-if="search.view === 'dest'" class="hw-dest-grid">
                <button
                  v-for="d in destTiles"
                  :key="d.city + d.country"
                  type="button"
                  class="hw-dest-tile"
                  @click="pickDestination(d.city)"
                >
                  <strong>{{ d.city }}</strong>
                  <span>{{ d.country }} · {{ d.total }} {{ $func.parseIso("weeksAvailableCount") }}</span>
                </button>
              </div>

              <template v-else>
                <div v-if="loading" class="hw-loading">
                  {{ $func.parseIso("loadingResortName") }}...
                </div>

                <template v-else>
                  <!-- Agotado: mensaje + Notify me + sugerencias cercanas -->
                  <div v-if="isSoldOutEmpty" class="hw-empty">
                    <h3>{{ $func.parseIso("soldOutTitle") }} {{ resultsTitle }}</h3>
                    <p>{{ $func.parseIso("soldOutBody") }}</p>
                    <button v-if="!isNotified" type="button" class="hw-search-btn" @click="notify">
                      {{ $func.parseIso("notifyMe") }}
                    </button>
                    <span v-else class="hw-notify-done">{{ $func.parseIso("notifyDone") }}</span>
                  </div>

                  <div v-else-if="weeks.length === 0" class="hw-empty">
                    <h3>{{ $func.parseIso("noResultsFilters") }}</h3>
                    <p>{{ $func.parseIso("noResultsBody") }}</p>
                    <button type="button" class="hw-search-btn" @click="clearAll">
                      {{ $func.parseIso("clearAll") }}
                    </button>
                  </div>

                  <template v-else>
                    <div class="hw-cards">
                      <ResortListItem v-for="(week, idx) in weeks" :week="week" :key="idx" :token="token" />
                    </div>

                    <div class="hw-paginate" v-if="totalPage > 1">
                      <button type="button" :disabled="search.page <= 1" @click="setPage(search.page - 1)">‹</button>
                      <span>{{ search.page }} / {{ totalPage }}</span>
                      <button type="button" :disabled="search.page >= totalPage" @click="setPage(search.page + 1)">›</button>
                    </div>
                  </template>
                </template>
              </template>
            </section>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "../components/Header.vue";
import ResortListItem from "../components/ResortList/ResortListItem.vue";
import HolidayFilters from "../components/HolidayList/HolidayFilters.vue";
import { holidayLabel } from "../utils/holidayTags";
import { getTokenTC, getClubObject } from "../utils/sesions";
import {
  getSearchHolidayObject,
  setSearchHolidayObject,
  modeOf,
  checkInWindow,
} from "../utils/holidaySearch";

const GROUP_I18N = {
  major: "majorHolidays",
  "long-weekend": "longWeekends",
  "school-break": "schoolBreaks",
};

const ALLOWED_COUNTRIES = ["USA", "Mexico", "Dominican Republic"];

export default {
  components: { Header, ResortListItem, HolidayFilters },
  data() {
    return {
      token: null,
      search: null,
      catalog: null,
      catalogLoading: false,
      countries: [],
      weeks: [],
      facets: {},
      totalList: 0,
      totalPage: 0,
      loading: false,
      notifiedMap: {},
      iso: localStorage.getItem("iso") || "es",
      panel: { holiday: "all", country: "", occupancy: "" },
      btnColor: null,
    };
  },
  computed: {
    mode() {
      return modeOf(this.search);
    },
    holMode() {
      return this.search && this.search.holiday === "all" ? "all" : "specific";
    },
    btnStyle() {
      return this.btnColor ? { backgroundColor: "#" + this.btnColor } : {};
    },
    catalogHolidays() {
      return this.catalog?.holidays || [];
    },
    groupsWithHolidays() {
      const groups = this.catalog?.groups || [];
      return groups.map((g) => ({
        ...g,
        holidays: this.catalogHolidays.filter((h) => h.group === g.id),
      }));
    },
    occurrence() {
      if (!this.search || this.search.holiday === "all") return null;
      const list = this.catalogHolidays;
      return (
        list.find(
          (h) => h.token === this.search.holiday && (!this.search.year || h.year === this.search.year)
        ) || list.find((h) => h.token === this.search.holiday) || null
      );
    },
    isLowInventory() {
      return this.occurrence?.state === "low";
    },
    isSoldOutEmpty() {
      return this.weeks.length === 0 && this.occurrence?.state === "soldout";
    },
    isNotified() {
      if (!this.occurrence) return false;
      return !!this.notifiedMap[this.occurrence.token + "|" + this.occurrence.year];
    },
    resultsTitle() {
      if (!this.search) return "";
      if (this.search.holiday === "all") return this.$func.parseIso("hwAllTitle");
      const h = this.occurrence;
      return h ? `${holidayLabel(h.token, this.iso)} ${h.yearLabel}` : this.search.holiday;
    },
    resultsSub() {
      if (!this.search) return "";
      if (this.search.holiday === "all") return this.$func.parseIso("holidayNext12");
      const h = this.occurrence;
      return h ? this.formatWhen(h) : "";
    },
    summaryHoliday() {
      return this.search?.holiday === "all" ? this.$func.parseIso("anyHoliday") : this.resultsTitle;
    },
    summaryDestination() {
      const s = this.search;
      if (!s) return "";
      if (s.country && s.city && s.city.length) return `${s.city.join(", ")}, ${s.country}`;
      if (s.country) return s.country;
      if (s.city && s.city.length) return s.city.join(", ");
      return this.$func.parseIso("anywhere");
    },
    summaryGuests() {
      return this.search?.occupancy ? this.search.occupancy + "+" : this.$func.parseIso("all");
    },
    destTiles() {
      return (this.facets?.ResortDest || []).filter((d) => d.city);
    },
    sortModel() {
      return this.search?.sort || "ci";
    },
  },
  watch: {
    "$route.query": {
      handler(newQuery, oldQuery) {
        // Solo re-inicia cuando cambia el punto de entrada (holiday/year), no
        // con cada filtro (los filtros viven en el search object)
        if (newQuery?.holiday !== oldQuery?.holiday || newQuery?.year !== oldQuery?.year) {
          this.initFromRoute();
        }
      },
    },
  },
  async created() {
    this.token = this.$route.query.token || getTokenTC();
    if (!this.token) {
      const urllogin = localStorage.getItem("UrlLogin");
      window.location.href = urllogin
        ? `https://${urllogin}`
        : `https://${process.env.VUE_APP_URL_LOGIN_DEFAULT}`;
      return;
    }

    try {
      this.notifiedMap = JSON.parse(localStorage.getItem("holidayNotified") || "{}");
    } catch (e) {
      this.notifiedMap = {};
    }

    await this.checkSesion();
    await this.loadCatalog();
    this.loadCountries();
    await this.initFromRoute();

    this._onChangeIso = () => {
      this.iso = localStorage.getItem("iso") || "es";
      this.$forceUpdate();
    };
    this.$bus.on("change-iso", this._onChangeIso);
  },
  beforeDestroy() {
    this.$bus.off("change-iso", this._onChangeIso);
  },
  methods: {
    holidayLabel, // expuesto para el template (panel y vitrina)
    async checkSesion() {
      try {
        const { data } = await this.$axios.get(`/auth/sesion?token=${this.token}`);
        if (data.success) {
          this.$bus.emit("sesion", { sesion: data.sesion, data: data.data });
          const __club = getClubObject();
          if (__club?.ColorPrimarioHex) this.btnColor = __club.ColorPrimarioHex;
        } else {
          if (data.sesion) window.location.href = `https://${data.sesion.UrlLogin}`;
          else window.location.href = `https://${process.env.VUE_APP_URL_LOGIN_DEFAULT}`;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async loadCatalog() {
      this.catalogLoading = true;
      try {
        const { data } = await this.$axios.get("/week/holidays");
        this.catalog = data;
      } catch (error) {
        console.log(error);
        this.catalog = { holidays: [], groups: [] };
      }
      this.catalogLoading = false;
    },
    async loadCountries() {
      try {
        const { data } = await this.$axios.get("/week/countries");
        if (!data || !Array.isArray(data.countries)) return;
        const all = data.countries.map((c) => c.Country);
        const fixed = ALLOWED_COUNTRIES.filter((c) => all.indexOf(c) !== -1);
        const rest = all.filter((c) => ALLOWED_COUNTRIES.indexOf(c) === -1).sort();
        this.countries = fixed.concat(rest);
      } catch (error) {
        console.log(error);
      }
    },
    initFromRoute() {
      this.search = getSearchHolidayObject(this.$route.query);
      if (this.mode === "results") {
        // Regreso al detalle debe volver aqui, no al listado normal
        sessionStorage.setItem("holidaylist", "y");
        this.listWeeks();
      } else {
        sessionStorage.removeItem("holidaylist");
        window.scrollTo(0, 0);
      }
    },
    buildPayload() {
      const s = this.search;
      const win = checkInWindow(s, this.occurrence);

      const payload = {
        checkInFrom: win.from,
        checkInTo: win.to,
      };

      if (s.holiday === "all") {
        payload.holidayTokens =
          s.holidayTokens && s.holidayTokens.length ? [...s.holidayTokens] : ["holiday"];
      } else {
        payload.holidayTokens = [s.holiday];
        if (s.checkInDates && s.checkInDates.length) payload.checkInDates = [...s.checkInDates];
      }

      if (s.country) payload.country = s.country;
      if (s.city && s.city.length) payload.city = [...s.city];
      if (s.occupancy) payload.occupancyMin = s.occupancy;
      if (s.unitType && s.unitType.length) payload.UnitType = [...s.unitType];
      if (s.priceMin != null && s.priceMin !== "") payload.PrecioMin = parseFloat(s.priceMin);
      if (s.priceMax != null && s.priceMax !== "") payload.PrecioMax = parseFloat(s.priceMax);
      if (s.rating) payload.Rating = s.rating;

      payload.sort = s.sort === "dest" ? { ResortCity: 1 } : { CheckInDate: 1 };
      return payload;
    },
    async listWeeks() {
      if (!this.search) return;
      this.loading = true;
      try {
        const { data } = await this.$axios.post(
          `/week/list?limit=${this.search.limit}&page=${this.search.page}&isPaginate=s`,
          this.buildPayload()
        );
        this.weeks = data.weeks;
        this.totalPage = data.paginate.totalPages;
        this.totalList = data.paginate.total;
        this.facets = data.filters || {};
      } catch (error) {
        console.log(error);
      }
      this.loading = false;
      window.scrollTo(0, 0);
    },
    persist() {
      setSearchHolidayObject(this.search);
    },
    async applyFilters(patch) {
      this.search = {
        ...this.search,
        holidayTokens: patch.holidayTokens,
        checkInDates: patch.checkInDates,
        city: patch.city,
        occupancy: patch.occupancy,
        unitType: patch.unitType,
        priceMin: patch.priceMin,
        priceMax: patch.priceMax,
        rating: patch.rating,
        page: 1,
      };
      this.persist();
      await this.listWeeks();
    },
    async setPage(p) {
      this.search.page = p;
      this.persist();
      await this.listWeeks();
    },
    async setView(v) {
      this.search.view = v;
      this.persist();
      this.$forceUpdate();
    },
    async onSortChange(e) {
      this.search.sort = e.target.value;
      this.persist();
      await this.listWeeks();
    },
    async pickDestination(city) {
      this.search.city = [city];
      this.search.view = "list";
      this.search.page = 1;
      this.persist();
      await this.listWeeks();
    },
    clearAll() {
      this.search = {
        ...this.search,
        city: [],
        holidayTokens: [],
        checkInDates: [],
        occupancy: null,
        unitType: [],
        priceMin: null,
        priceMax: null,
        rating: null,
        page: 1,
      };
      this.persist();
      this.listWeeks();
    },
    goToResults(h) {
      const query = { ...this.$route.query, holiday: h.token, year: h.year };
      // La query siembra (y persiste) el search object
      this.search = getSearchHolidayObject(query);
      const same =
        this.$route.query.holiday === query.holiday &&
        String(this.$route.query.year) === String(query.year);
      if (same) this.listWeeks();
      else this.$router.push({ query }).catch(() => {});
    },
    goHub() {
      const query = { ...this.$route.query };
      delete query.holiday;
      delete query.year;
      this.search = null;
      localStorage.removeItem("searchHoliday");
      this.$router.push({ query }).catch(() => {});
    },
    submitPanel() {
      const value = this.panel.holiday;
      const query = { ...this.$route.query };
      if (value === "all") {
        query.holiday = "all";
        delete query.year;
      } else {
        const [token, year] = value.split("|");
        query.holiday = token;
        query.year = year;
      }
      if (this.panel.country) query.country = this.panel.country;
      else delete query.country;
      if (this.panel.occupancy) query.occupancy = this.panel.occupancy;
      else delete query.occupancy;

      this.search = getSearchHolidayObject(query); // persiste
      const same =
        this.$route.query.holiday === query.holiday &&
        String(this.$route.query.year) === String(query.year);
      if (same) this.listWeeks();
      else this.$router.push({ query }).catch(() => {});
    },
    stateLabel(state) {
      if (state === "soldout") return this.$func.parseIso("hwSoldOut");
      if (state === "low") return this.$func.parseIso("hwLastWeeks");
      return this.$func.parseIso("hwAvailable");
    },
    countLabel(h) {
      if (h.state === "soldout") return this.$func.parseIso("notifyMe");
      return `${h.count} ${this.$func.parseIso("weeksAvailableCount")}`;
    },
    groupTitle(id) {
      const key = GROUP_I18N[id];
      return key ? this.$func.parseIso(key) : id;
    },
    formatWhen(h) {
      if (h.group === "school-break") {
        const from = this.$moment(h.from).format("D MMM");
        const to = this.$moment(h.to).format("D MMM");
        return `${from} – ${to}`;
      }
      return this.$moment(h.date).format("ddd, D MMM");
    },
    async notify() {
      if (!this.occurrence) return;
      try {
        await this.$axios.post("/week/holiday-notify", {
          token: this.token,
          holiday: this.occurrence.token,
          year: this.occurrence.year,
        });
        // $set: en Vue 2 la adicion de propiedades no es reactiva sin esto
        this.$set(this.notifiedMap, this.occurrence.token + "|" + this.occurrence.year, true);
        localStorage.setItem("holidayNotified", JSON.stringify(this.notifiedMap));
      } catch (error) {
        console.log(error);
      }
    },
  },
};

// La vista usa $func.parseIso para todos los textos
</script>

<style scoped>
.hw-page {
  min-height: 60vh;
}
.hw-main {
  padding: 24px 16px 48px;
  max-width: 1180px;
  margin: 0 auto;
}
.hw-title {
  font-size: 24px;
  font-weight: 600;
  color: #393939;
  margin: 0 0 4px;
}
.hw-sub {
  color: #5a6670;
  margin: 0;
  font-size: 14px;
}
.hw-hint {
  color: #8a97a0;
  font-size: 12px;
}
.hw-hub-head {
  margin-bottom: 18px;
}
.hw-panel {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  align-items: end;
  background: #fff;
  border: 1px solid #e1e6ea;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 26px;
}
.hw-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.hw-field label {
  font-size: 12px;
  color: #5a6670;
}
.hw-field select {
  height: 40px;
  border: 1px solid #dde3ea;
  border-radius: 8px;
  padding: 0 10px;
  background: #fff;
  font-size: 13.5px;
  width: 100%;
}
.hw-search-btn {
  height: 40px;
  border: 0;
  border-radius: 8px;
  background: #0472aa;
  color: #fff;
  font-weight: 600;
  padding: 0 20px;
  cursor: pointer;
  white-space: nowrap;
}
.hw-search-btn.sm {
  height: 34px;
  padding: 0 14px;
  font-size: 13px;
}
.hw-search-btn:disabled {
  opacity: 0.6;
  cursor: wait;
}
.hw-group {
  margin-bottom: 26px;
}
.hw-group-head h2 {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px;
}
.hw-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
}
.hw-grid.compact {
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}
.hw-card {
  position: relative;
  display: flex;
  flex-direction: column;
  text-align: left;
  border: 1px solid #e1e6ea;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  min-width: 0;
}
.hw-card:hover {
  border-color: #0472aa;
  box-shadow: 0 1px 2px rgba(20, 40, 55, 0.06), 0 4px 14px rgba(20, 40, 55, 0.06);
}
.hw-card-when {
  display: block;
  padding: 10px 12px;
  color: #fff;
  font-size: 12.5px;
  font-weight: 500;
  background: linear-gradient(135deg, #b23a48, #7a2a35);
}
.hw-card.compact .hw-card-when {
  background: linear-gradient(135deg, #00527c, #0472aa);
}
.hw-card.soldout .hw-card-when {
  filter: grayscale(0.85);
}
.hw-next-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background: #fff;
  color: #7a2a35;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 999px;
}
.hw-card-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 12px 14px 14px;
}
.hw-card-name {
  font-weight: 600;
  font-size: 15px;
  color: #393939;
}
.hw-card.soldout .hw-card-name {
  color: #5a6670;
}
.hw-card-year {
  color: #5a6670;
  font-size: 12.5px;
}
.hw-card-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  font-size: 12.5px;
  color: #5a6670;
}
.hw-state {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 9px;
  border-radius: 999px;
}
.hw-state i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.hw-state.ok {
  background: #e6f4e1;
  color: #2f6f1f;
}
.hw-state.low {
  background: #fff2d6;
  color: #7d5200;
}
.hw-state.soldout {
  background: #e9edf0;
  color: #59636b;
}
.hw-crumb {
  font-size: 13px;
  color: #5a6670;
  margin-bottom: 6px;
}
.hw-crumb a {
  color: #00527c;
}
.hw-crumb-sep {
  margin: 0 6px;
}
.hw-alt-row {
  margin-top: 10px;
  font-size: 13px;
  text-align: center;
}
.hw-alt-row a {
  color: #00527c;
}
.hw-res-head {
  margin-bottom: 12px;
}
.hw-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 22px;
  background: #f4f7f9;
  border: 1px solid #e1e6ea;
  border-radius: 10px;
  padding: 10px 14px;
  margin: 14px 0 16px;
}
.hw-summary div {
  display: flex;
  flex-direction: column;
}
.hw-summary small {
  font-size: 11px;
  color: #5a6670;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.hw-summary strong {
  font-weight: 500;
}
.hw-modify {
  margin-left: auto;
  height: 34px;
  background: transparent;
  color: #00527c;
  border: 1px solid #0472aa;
  border-radius: 8px;
  padding: 0 14px;
  font-size: 13px;
  cursor: pointer;
}
.hw-banner {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  background: #fff2d6;
  border: 1px solid #efd08a;
  color: #7d5200;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
}
.hw-banner span {
  display: block;
  font-size: 13px;
  margin-top: 1px;
}
.hw-notify-done {
  font-weight: 600;
  color: #2f6f1f;
}
.hw-layout {
  display: grid;
  grid-template-columns: 250px minmax(0, 1fr);
  gap: 22px;
  align-items: start;
}
.hw-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.hw-count {
  color: #5a6670;
}
.hw-count b {
  color: #393939;
}
.hw-bar-right {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.hw-seg {
  display: inline-flex;
  border: 1px solid #dde3ea;
  border-radius: 8px;
  overflow: hidden;
}
.hw-seg button {
  border: 0;
  background: #fff;
  padding: 7px 12px;
  font-size: 13px;
  cursor: pointer;
}
.hw-seg button.on {
  background: #e7f3fa;
  color: #00527c;
  font-weight: 600;
}
.hw-sort {
  height: 36px;
  border: 1px solid #dde3ea;
  border-radius: 8px;
  padding: 0 10px;
  background: #fff;
  font-size: 13px;
}
.hw-cards {
  display: block;
}
.hw-paginate {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
  margin-top: 16px;
}
.hw-paginate button {
  border: 1px solid #dde3ea;
  background: #fff;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  cursor: pointer;
}
.hw-paginate button:disabled {
  opacity: 0.4;
  cursor: default;
}
.hw-dest-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
}
.hw-dest-tile {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 2px;
  min-height: 110px;
  padding: 14px;
  border: 0;
  border-radius: 12px;
  color: #fff;
  text-align: left;
  cursor: pointer;
  background: linear-gradient(135deg, #00527c, #0472aa);
}
.hw-dest-tile strong {
  font-size: 16px;
}
.hw-dest-tile span {
  font-size: 12.5px;
  opacity: 0.9;
}
.hw-empty {
  border: 1px dashed #dde3ea;
  border-radius: 12px;
  padding: 30px 20px;
  text-align: center;
}
.hw-empty h3 {
  font-size: 18px;
  margin: 0 0 6px;
}
.hw-empty p {
  color: #5a6670;
  max-width: 52ch;
  margin: 0 auto 16px;
}
.hw-loading {
  padding: 30px 20px;
  text-align: center;
  color: #5a6670;
}
@media (max-width: 820px) {
  .hw-layout {
    grid-template-columns: 1fr;
  }
}
</style>
